import { API_BASE_URL } from '../config/api.config';

const GUEST_CART_KEY = 'guestCart';
const GUEST_FAVS_KEY = 'guestFavorites';

export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user'));
  } catch {
    return null;
  }
}

function notifyCartUpdate() {
  window.dispatchEvent(new Event('guest-cart-updated'));
}

function notifyFavoritesUpdate() {
  window.dispatchEvent(new Event('guest-favorites-updated'));
}

export function getGuestCart() {
  try {
    const raw = JSON.parse(localStorage.getItem(GUEST_CART_KEY));
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function saveGuestCart(cart) {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
  notifyCartUpdate();
}

export function addToGuestCart(product, quantity = 1) {
  const cart = getGuestCart();
  const productId = Number(product.id);
  const qty = Math.max(1, Number(quantity) || 1);
  const existing = cart.find((item) => Number(item.product_id) === productId);

  if (existing) {
    existing.quantity += qty;
  } else {
    cart.push({
      id: `guest-${productId}`,
      product_id: productId,
      name: product.name,
      price: parseFloat(product.price) || 0,
      quantity: qty,
      image: product.image || '',
    });
  }

  saveGuestCart(cart);
  return cart;
}

export function removeFromGuestCart(productId) {
  const cart = getGuestCart().filter((item) => Number(item.product_id) !== Number(productId));
  saveGuestCart(cart);
  return cart;
}

export function updateGuestCartQuantity(productId, quantity) {
  const cart = getGuestCart();
  const id = Number(productId);
  const qty = Number(quantity);

  if (!Number.isFinite(qty) || qty <= 0) {
    return removeFromGuestCart(id);
  }

  const next = cart.map((item) =>
    Number(item.product_id) === id ? { ...item, quantity: qty } : item
  );
  saveGuestCart(next);
  return next;
}

export function isProductInCart(productId) {
  return getGuestCart().some((item) => Number(item.product_id) === Number(productId));
}

export function getCartProductIds() {
  return getGuestCart().map((item) => Number(item.product_id));
}

export function getGuestCartCount() {
  return getGuestCart().length;
}

export function clearGuestCart() {
  localStorage.removeItem(GUEST_CART_KEY);
  notifyCartUpdate();
}

export function getGuestFavorites() {
  try {
    const raw = JSON.parse(localStorage.getItem(GUEST_FAVS_KEY));
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function saveGuestFavorites(favorites) {
  localStorage.setItem(GUEST_FAVS_KEY, JSON.stringify(favorites));
  notifyFavoritesUpdate();
}

export function isGuestFavorite(productId) {
  return getGuestFavorites().some((item) => Number(item.id) === Number(productId));
}

export function addGuestFavorite(product) {
  const favorites = getGuestFavorites();
  const productId = Number(product.id);
  if (favorites.some((item) => Number(item.id) === productId)) {
    return favorites;
  }

  favorites.push({
    id: productId,
    name: product.name,
    description: product.description || '',
    price: parseFloat(product.price) || 0,
    image: product.image || '',
  });

  saveGuestFavorites(favorites);
  return favorites;
}

export function removeGuestFavorite(productId) {
  const favorites = getGuestFavorites().filter((item) => Number(item.id) !== Number(productId));
  saveGuestFavorites(favorites);
  return favorites;
}

export function clearGuestFavorites() {
  localStorage.removeItem(GUEST_FAVS_KEY);
  notifyFavoritesUpdate();
}

export async function updateCartQuantity(productId, quantity) {
  const user = getStoredUser();
  if (!user) {
    return { ok: true, guest: true, cart: updateGuestCartQuantity(productId, quantity) };
  }

  const response = await fetch(`${API_BASE_URL}?action=update_cart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId: user.id,
      productId,
      quantity,
    }),
  });
  const data = await response.json();
  return { ok: data.status === 'ok', guest: false, message: data.message };
}

export async function fetchCartProductIds() {
  const user = getStoredUser();
  if (!user) {
    return getCartProductIds();
  }

  try {
    const response = await fetch(`${API_BASE_URL}?action=cart&userId=${user.id}`);
    const data = await response.json();
    if (data.status === 'ok') {
      return (data.cart || []).map((item) => Number(item.product_id));
    }
  } catch {
    /* ignore */
  }
  return [];
}

export async function addProductToCart(product, quantity = 1) {
  const user = getStoredUser();
  if (!user) {
    addToGuestCart(product, quantity);
    return { ok: true, guest: true };
  }

  const response = await fetch(`${API_BASE_URL}?action=add_to_cart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId: user.id,
      productId: product.id,
      quantity,
    }),
  });
  const data = await response.json();
  return { ok: data.status === 'ok', guest: false, message: data.message };
}

export async function toggleProductFavorite(product, isFavorite) {
  const user = getStoredUser();
  if (!user) {
    if (isFavorite) {
      removeGuestFavorite(product.id);
    } else {
      addGuestFavorite(product);
    }
    return { ok: true, guest: true, isFavorite: !isFavorite };
  }

  const action = isFavorite ? 'remove_favourite' : 'add_favourite';
  const response = await fetch(`${API_BASE_URL}?action=${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId: user.id,
      productId: product.id,
    }),
  });
  const data = await response.json();
  return { ok: data.status === 'ok', guest: false, isFavorite: !isFavorite, message: data.message };
}

export async function mergeGuestDataOnLogin(userId) {
  const guestCart = getGuestCart();
  for (const item of guestCart) {
    try {
      await fetch(`${API_BASE_URL}?action=add_to_cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          productId: item.product_id,
          quantity: item.quantity,
        }),
      });
    } catch {
      /* best effort */
    }
  }
  if (guestCart.length) clearGuestCart();

  const guestFavs = getGuestFavorites();
  for (const item of guestFavs) {
    try {
      await fetch(`${API_BASE_URL}?action=add_favourite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          productId: item.id,
        }),
      });
    } catch {
      /* best effort */
    }
  }
  if (guestFavs.length) clearGuestFavorites();
}
